import clsx from 'clsx'
import Image from 'components/Common/Image'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import AspectRatio from 'components/utils/AspectRatio'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import TextButton from 'LuxKit/Button/TextButton'
import TextButtonLoadingSkeleton from 'LuxKit/Button/TextButtonLoadingSkeleton'
import Carousel from 'LuxKit/Carousel/Carousel'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import LineCalendarHeartIcon from 'LuxKit/Icons/line/LineCalendarHeartIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import LineHeartCardIcon from 'LuxKit/Icons/line/LineHeartCardIcon'
import LineLaptopIcon from 'LuxKit/Icons/line/LineLaptopIcon'
import LineLinkIcon from 'LuxKit/Icons/line/LineLinkIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTreesIcon from 'LuxKit/Icons/line/LineTreesIcon'
import LineUserMdIcon from 'LuxKit/Icons/line/LineUserMdIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import LineWaterIcon from 'LuxKit/Icons/line/LineWaterIcon'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import PaletteSheet from 'LuxKit/PaletteSheet'
import TextLink from 'LuxKit/TextLink'
import ToggleButton from 'LuxKit/ToggleButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import variables from 'styles/vendor/bootstrap/variables'
import WeddingQuoteOpenIcon from 'weddings/components/WeddingQuoteOpenIcon'
import WeddingPageHeader, { type WeddingNavItem } from 'weddings/components/WeddingPageHeader'
import LineCommentInfoIcon from 'LuxKit/Icons/line/LineCommentInfoIcon'
import LineFileCheckAltIcon from 'LuxKit/Icons/line/LineFileCheckAltIcon'
import Accordion from 'LuxKit/Accordion'
import Divider from 'LuxKit/Divider'
import LineCalendarCheckIcon from 'LuxKit/Icons/line/LineCalendarCheckIcon'
import NumberRating from 'components/Common/NumberRating'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import ModalContext from 'contexts/ModalContext'
import WeddingVenueInspirationModal from 'weddings/components/LandingPage/WeddingVenueInspirationModal'
import WeddingVenueSeason from 'weddings/components/LandingPage/WeddingVenueSeason'
import { scrollToSection } from 'weddings/lib/scrollUtils'
import WeddingLandingPageSupportModal from 'weddings/components/LandingPage/WeddingLandingPageSupportModal'
import wait from 'lib/promise/wait'
import VimeoVideo from 'components/Common/Video/VimeoVideo'
import useVenuesList, { type WeddingVenueInspiration } from 'weddings/hooks/useVenuesList'
import type { VenueInspirationOption } from 'weddings/constants/venuesList'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'

const SAMPLE_COUPLE_PAGE_URL = 'https://luxuryescapes.com/weddings/jane-and-mathew-aug-2026'
const BOOK_FREE_CONSULTATION_URL = 'https://luxuryescapes.com/inspiration/destination-weddings/enquire/'
const WEDDING_SETUP_PATH = '/weddings/create'

const Root = styled.div`
  position: relative;
`

const MainPhotoSection = styled.div`
  margin: 0 auto;
  width: 100%;
`

const HeadingSection = styled(Sheet)`
  position: relative;
  z-index: 1;
  margin-top: ${rem(-60)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(-180)};
    box-shadow: ${props => props.theme.shadow.bottom.large};
  }
`

const ImageWrapper = styled.div`
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.L};
  width: var(--width);
  min-width: var(--width);
  height: var(--height);
  max-height: var(--height);

  &.full-width {
    width: 100%;
    min-width: 100%;
  }

  &.full-height {
    height: 100%;
    min-height: 100%;
  }

  &.borderless {
    border-radius: 0;
  }

  ${mediaQueryUp.desktop} {
    &:not(.full-width) {
      width: var(--desktop-width);
      min-width: var(--desktop-width);
    }
    &:not(.full-height) {
      height: var(--desktop-height);
      max-height: var(--desktop-height);
    }
  }

  ${mediaQueryUp.largeDesktop} {
    &:not(.full-width) {
      width: var(--large-desktop-width);
      min-width: var(--large-desktop-width);
    }
    &:not(.full-height) {
      height: var(--large-desktop-height);
      max-height: var(--large-desktop-height);
    }
  }
`

const LoyaltyCoinsContainer = styled(Group)`
  padding: ${rem(12)} ${rem(26)};

  > :first-child {
    transform: translate(${rem(6.4)}, ${rem(-8)});
  }

  > :nth-child(2) {
    z-index: 1;
    transform: translate(${rem(-6.4)}, ${rem(8)});
  }
`

const HoneymoonPlanningSectionLayoutContainer = styled(LayoutContainer)`
  height: ${rem(663)};

  .image-wrapper {
    position: absolute;
    top: 0;
    inset-inline-start: 0;
    inset-inline-end: 0;
    bottom: 0;
    z-index: -1;

    >:nth-child(n) {
      width: 100%;
      height: 100%;
    }
  }

  ${mediaQueryUp.tablet} {
    height: auto;

    .image-wrapper {
      margin-inline: ${rem(variables.gutterWidth / 2)};
    }

    .content-container {
      max-width: ${rem(456)};
    }
  }
`

const VenueInspirationToggleButton = styled(ToggleButton)`
  border-radius: ${props => props.theme.borderRadius.round};
  font-weight: bold;
`

const VenueInspirationImagesWrapper = styled(Group)`
  padding: ${rem(12)};

  .rounded-image-wrapper {
    border: ${rem(3)} solid ${props => props.theme.palette.neutral.surface.darkContrast};

    &:nth-child(1) {
      transform: rotate(-15deg) translate(${rem(6.4)}, ${rem(-8)});
    }

    &:nth-child(2) {
      transform: rotate(8deg) translate(${rem(-6.4)}, ${rem(8)});
    }
  }
`

const VenueInspirationTile = styled(Sheet)`
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;

  .tile-content {
    height: 100%;
  }
`

const VenueInspirationLoadingTile = styled(Sheet)`
  position: relative;
  overflow: hidden;
  width: ${rem(280)};
  height: 100%;
  min-height: ${rem(470)};

  ${mediaQueryUp.desktop} {
    width: ${rem(480)};
  }

  .image-placeholder {
    width: 100%;
    height: ${rem(190)};

    ${mediaQueryUp.desktop} {
      height: ${rem(260)};
    }
  }
`

const VenueInspirationTileGallery = styled(ImageGallery)`
  width: ${rem(280)};

  ${mediaQueryUp.desktop} {
    width: ${rem(480)};
  }
`

const VenueInspirationCTATile = styled(Sheet)`
  width: ${rem(280)};
  height: 100%;

  ${mediaQueryUp.desktop} {
    width: ${rem(480)};
  }
`

const VenueInspirationCTABannerContainer = styled(Sheet)`
  margin-inline: ${rem(-(variables.gutterWidth / 2))};
  min-width: 100%;

  ${mediaQueryUp.tablet} {
    margin-inline: auto;
  }
`

const ReviewContainer = styled(Sheet)`
  overflow: hidden;

  .image-wrapper {
    ${mediaQueryDown.tablet} {
      width: 100%;

      >:nth-child(n) {
        width: 100%;
      }
    }

    ${mediaQueryUp.desktop} {
      height: 100%;

      >:nth-child(n) {
        height: 100%;
      }
    }
  }
`

const GiftRegistryImagesContainer = styled.div`
  position: relative;
  width: ${rem(335)};
  min-width: ${rem(335)};
  min-height: ${rem(252)};

  .left-image, .main-image, .right-image {
    position: absolute;
    box-shadow: ${props => props.theme.shadow.bottom.medium};
    border-radius: ${props => props.theme.borderRadius.L};
    overflow: hidden;
  }

  .left-image {
    width: ${rem(75)};
    height: ${rem(69)};
    top: 9.39%;
    inset-inline-start: 2.6%;
    border: ${rem(2)} solid ${props => props.theme.palette.neutral.surface.darkContrast};
  }

  .main-image {
    width: ${rem(265)};
    height: ${rem(172)};
    top: 24.6%;
    inset-inline-start: 10.2%;
    z-index: 1;
  }

  .right-image {
    width: ${rem(85)};
    height: ${rem(86)};
    top: 2.69%;
    inset-inline-start: 70.8%;
    border: ${rem(2)} solid ${props => props.theme.palette.neutral.surface.darkContrast};
    z-index: 2;
  }

  ${mediaQueryUp.desktop} {
    min-width: ${rem(500)};
    min-height: ${rem(428)};

    .left-image {
      width: ${rem(118)};
      height: ${rem(110)};
    }

    .main-image {
      width: ${rem(418)};
      height: ${rem(272)};
    }

    .right-image {
      width: ${rem(135)};
      height: ${rem(136)};
    }
  }

  ${mediaQueryUp.largeDesktop} {
    min-width: ${rem(570)};

    .left-image {
      width: ${rem(128)};
      height: ${rem(118)};
    }

    .main-image {
      width: ${rem(460)};
      height: ${rem(300)};
    }

    .right-image {
      width: ${rem(145)};
      height: ${rem(146)};
    }
  }
`

function RoundedImage({
  imageId,
  width,
  height,
  fullWidth,
  fullHeight,
  alt,
  desktopWidth,
  desktopHeight,
  largeDesktopWidth,
  largeDesktopHeight,
  borderless,
  fit,
  format,
}: {
  imageId: string,
  width: number,
  height: number,
  fullWidth?: boolean,
  fullHeight?: boolean,
  alt?: string,
  desktopWidth?: number,
  desktopHeight?: number,
  largeDesktopWidth?: number,
  largeDesktopHeight?: number,
  borderless?: boolean,
  fit?: React.ComponentProps<typeof Image>['fit'],
  format?: React.ComponentProps<typeof Image>['format'],
}) {
  const isDesktop = useScreenSizeUp('desktop')
  const isLargeDesktop = useScreenSizeUp('largeDesktop')

  const sizes = useMemo(() => ({
    width: rem(width),
    height: rem(height),
    desktopWidth: rem(desktopWidth ?? width),
    desktopHeight: rem(desktopHeight ?? height),
    largeDesktopWidth: rem(largeDesktopWidth ?? desktopWidth ?? width),
    largeDesktopHeight: rem(largeDesktopHeight ?? desktopHeight ?? height),
  }), [width, height, desktopWidth, desktopHeight, largeDesktopWidth, largeDesktopHeight])

  const { imageWidth, imageHeight } = useMemo(() => {
    let finalWidth = width
    let finalHeight = height

    if (isLargeDesktop) {
      if (largeDesktopWidth) finalWidth = largeDesktopWidth
      if (largeDesktopHeight) finalHeight = largeDesktopHeight
    }

    if (!isLargeDesktop && isDesktop) {
      if (desktopWidth) finalWidth = desktopWidth
      if (desktopHeight) finalHeight = desktopHeight
    }

    return {
      imageWidth: finalWidth,
      imageHeight: finalHeight,
    }
  }, [width, height, desktopWidth, desktopHeight, largeDesktopWidth, largeDesktopHeight, isDesktop, isLargeDesktop])

  return (
    <ImageWrapper
      className={clsx('rounded-image-wrapper', { 'full-width': fullWidth, 'full-height': fullHeight, borderless })}
      style={{
        '--width': sizes.width,
        '--height': sizes.height,
        '--desktop-width': sizes.desktopWidth,
        '--desktop-height': sizes.desktopHeight,
        '--large-desktop-width': sizes.largeDesktopWidth,
        '--large-desktop-height': sizes.largeDesktopHeight,
      }}
    >
      <Image
        id={imageId}
        alt={alt}
        quality="good"
        objectFit="cover"
        fit={fit ?? 'center'}
        width={imageWidth}
        height={imageHeight}
        format={format}
      />
    </ImageWrapper>
  )
}

const SECTION_IDS = {
  WEDDINGS: 'weddings',
  GIFT_REGISTRY_AND_WEBSITE: 'gift-registry-and-website',
  HONEYMOON: 'honeymoon',
  INSPIRATION: 'inspiration',
  FAQS: 'faqs',
  HIGHLIGHTS: 'highlights',
  CONCIERGE: 'concierge',
} as const

const venueInspirationTileImageProps: ImageParams = {
  mobileAspectRatio: '28:19',
  desktopAspectRatio: '24:13',
  quality: 'good',
}

function WeddingsLandingPage() {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const isAboveDesktop = useScreenSizeUp('desktop')

  const [selectedVenueInspiration, setSelectedVenueInspiration] = useState<VenueInspirationOption>('featured')
  const [isSupportModalVisible, setIsSupportModalVisible] = useState(false)
  const [isSupportModalDismissed, setIsSupportModalDismissed] = useState(false)

  const showModal = useContext(ModalContext)
  const { venues: venueInspirations, isLoading: isVenueInspirationsLoading } = useVenuesList(selectedVenueInspiration)

  const navigationItems = useMemo<Array<WeddingNavItem>>(() => ([
    {
      id: SECTION_IDS.WEDDINGS,
      label: intl.formatMessage({
        id: 'CARC9FqjfdO_4Js-DmUs',
        defaultMessage: 'Weddings',
        description: "Label for the 'Weddings' nav link in the weddings landing page header",
      }),
    },
    {
      id: SECTION_IDS.GIFT_REGISTRY_AND_WEBSITE,
      label: intl.formatMessage({
        id: 'aJWGymIHdF4ufyVuKXmq',
        defaultMessage: 'Gift registry & website',
        description: "Label for the 'Gift registry & website' nav link in the weddings landing page header",
      }),
    },
    {
      id: SECTION_IDS.HONEYMOON,
      label: intl.formatMessage({
        id: 'JZVVt2sKlywSbRFT3D3k',
        defaultMessage: 'Honeymoon',
        description: "Label for the 'Honeymoon' nav link in the weddings landing page header",
      }),
    },
    {
      id: SECTION_IDS.INSPIRATION,
      label: intl.formatMessage({
        id: '2GQSChylHRLz0xjAvBNk',
        defaultMessage: 'Inspiration',
        description: "Label for the 'Inspiration' nav link in the weddings landing page header",
      }),
    },
    {
      id: SECTION_IDS.FAQS,
      label: intl.formatMessage({
        id: 'yAnFpCuNwbgtMIFQ_8DK',
        defaultMessage: 'FAQs',
        description: "Label for the 'FAQs' nav link in the weddings landing page header",
      }),
    },
  ]), [intl])

  const bookConsultationButtonLabel = intl.formatMessage({
    id: 'BdvswPsw6j9RR1bzPesM',
    defaultMessage: 'Book a free consultation',
    description: 'Label for the CTA button on the weddings landing page',
  })

  const onBookConsultationClick = useCallback(() => {
    window.open(BOOK_FREE_CONSULTATION_URL, '_blank')
  }, [])

  const highlights = useMemo(() => {
    const destinationAndVenueSelection = {
      imageId: 'wedding-landing-page-highlight-1',
      title: intl.formatMessage({
        id: 'cf7Gz9_NrA39Bp_a2M30',
        defaultMessage: 'Destination & venue selection',
        description: "Title for the 'Destination & venue selection' highlight on the weddings landing page",
      }),
      description: intl.formatMessage({
        id: 'Y61gDRGKEQ6hRFqWjMl3',
        defaultMessage: 'We match you to venues that suit how you want to celebrate – then handle all the travel logistics for you and your guests.',
        description: "Description for the 'Destination & venue selection' highlight on the weddings landing page",
      }),
      scrollToSection: SECTION_IDS.WEDDINGS,
    }

    const giftRegistryAndWeddingsWebsite = {
      imageId: 'wedding-landing-page-highlight-2',
      title: intl.formatMessage({
        id: 'wGmEZLScPTYEOQZ4MYnQ',
        defaultMessage: 'Gift registry & wedding website',
        description: "Title for the 'Gift registry & wedding website' highlight on the weddings landing page",
      }),
      description: intl.formatMessage({
        id: '6a656gpBOkJaTqT4UvAG',
        defaultMessage: 'A personalised wedding website gives guests everything they need: event details, travel information, and a place to contribute to your registry or honeymoon fund.',
        description: "Description for the 'Gift registry & wedding website' highlight on the weddings landing page",
      }),
      scrollToSection: SECTION_IDS.GIFT_REGISTRY_AND_WEBSITE,
    }

    const honeymoonInspirationAndPlanning = {
      imageId: 'wedding-landing-page-highlight-3',
      title: intl.formatMessage({
        id: 'wnJg4RzZ09ny8T0I6XdG',
        defaultMessage: 'Honeymoon inspiration & planning',
        description: "Title for the 'Honeymoon inspiration & planning' highlight on the weddings landing page",
      }),
      description: intl.formatMessage({
        id: '90GabY5X-xIgn3ohbXME',
        defaultMessage: "Bespoke itineraries for wherever you've always wanted to go next. From the destination to the room, everything arranged before you arrive.",
        description: "Description for the 'Honeymoon inspiration & planning' highlight on the weddings landing page",
      }),
      scrollToSection: SECTION_IDS.HONEYMOON,
    }

    const dedicatedConciergeAndSupport = {
      imageId: 'wedding-landing-page-highlight-4',
      title: intl.formatMessage({
        id: 'HNZLJQpbiBMHudOakxJ8',
        defaultMessage: 'Dedicated concierge & support',
        description: "Title for the 'Dedicated concierge & support' highlight on the weddings landing page",
      }),
      description: intl.formatMessage({
        id: 'qCqQHNScDPLTkHfxVen4',
        defaultMessage: 'One team handles guest questions, booking enquiries and travel requests from the moment you confirm to the day you leave.',
        description: "Description for the 'Dedicated concierge & support' highlight on the weddings landing page",
      }),
      scrollToSection: SECTION_IDS.CONCIERGE,
    }

    return {
      destinationAndVenueSelection,
      giftRegistryAndWeddingsWebsite,
      honeymoonInspirationAndPlanning,
      dedicatedConciergeAndSupport,
      list: [destinationAndVenueSelection, giftRegistryAndWeddingsWebsite, honeymoonInspirationAndPlanning, dedicatedConciergeAndSupport],
    }
  }, [intl])

  const visibleVenueInspirations = useMemo<Array<WeddingVenueInspiration>>(() => venueInspirations.filter((option) => (
    option.images.length > 0
  )), [venueInspirations])
  const shouldShowVenueInspirationsLoading = isVenueInspirationsLoading && venueInspirations.length > 0 && visibleVenueInspirations.length === 0
  const hasVenueInspirationsSection = visibleVenueInspirations.length > 0 || shouldShowVenueInspirationsLoading

  const faqs = useMemo(() => [
    {
      question: intl.formatMessage({ id: 'qhFkIwidBKTIFEtNzb2-', defaultMessage: 'What will your wedding concierge help us with?', description: 'Question for the FAQs section on the weddings landing page' }),
      answer: intl.formatMessage(
        {
          id: 'LEWHuKgY4XuWsXhAZAhE',
          defaultMessage: `Our dedicated wedding concierge is here to make your travel and planning stress-free. They can assist with:<lineBreak></lineBreak>
• Flights and airport transfers
• Booking your accommodation
• Details about the wedding venue and schedule
• Organizing excursions or experiences before/after the wedding
• Helping with trip extensions if you’d like to stay longer
• Coordinating the gift registry
• Providing honeymoon planning support… and more! Don’t hesitate to reach out — they’re your go-to for anything travel or event-related`,
          description: 'Answer for the FAQs section on the weddings landing page',
        },
        {
          lineBreak: () => <br></br>,
        },
      ),
    },
    {
      question: intl.formatMessage({
        id: 'caVz9U17Sgjw0QK-dDOn',
        defaultMessage: 'Who can use the concierge service — just the couple, or guests too?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage({
        id: 'citRNvzCozuGqkGOMiqH',
        defaultMessage: 'Everyone! While couples receive end-to-end planning support, guests also benefit — our concierge is here to help with their travel arrangements, questions, and preferences.',
        description: 'Answer for the FAQs section on the weddings landing page',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'f0qdOgKdy0dowfXPR2LV',
        defaultMessage: 'How do we contact the wedding concierge?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage({
        id: 'svNmgD73R9xylUCctDnx',
        defaultMessage: 'Whether you’re just starting to plan or ready to book, our wedding concierge is here to help. Contact the team by email, phone or WhatsApp at any time.',
        description: 'Answer for the FAQs section on the weddings landing page',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'cMx0xot4ZHgUYR1HLX-l',
        defaultMessage: 'How do you help keep everything within our budget?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage({
        id: 'KX0Bm4I9O4CCJkjsmlT7',
        defaultMessage: 'We’ll recommend destinations and venues that fit your budget, negotiate group rates, and offer payment plans. Our concierge also helps guests book within their means, reducing pressure on the couple.',
        description: 'Answer for the FAQs section on the weddings landing page',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'knzcHaTfWHsyC8tmCxfT',
        defaultMessage: 'Does it cost extra to use the concierge?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage({
        id: 'xI_spZ2KTiwL3hxZvnZn',
        defaultMessage: 'No additional cost for you or your guests — concierge services are included as part of our wedding planning packages.',
        description: 'Answer for the FAQs section on the weddings landing page',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'colJ8LGTm3VfWkzxFlXG',
        defaultMessage: 'Can the concierge help with last-minute changes or emergencies?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage({
        id: 'KOMJb2k9aVZLnr8UqAPC',
        defaultMessage: 'Absolutely. Whether it’s a flight issue, room requests, or lost luggage, your concierge is on standby to help troubleshoot before and during the trip.',
        description: 'Answer for the FAQs section on the weddings landing page',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'pVnHL4Gh42lVs2zYdY9l',
        defaultMessage: 'How far in advance should we plan a destination wedding?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage({
        id: '2WiTCikodiLsmuh5Jryv',
        defaultMessage: 'Ideally 6–18 months in advance. This allows time to secure your preferred venue, lock in group rates, and give your guests plenty of notice.',
        description: 'Answer for the FAQs section on the weddings landing page',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'ozjZeG8Om8-RbvRMpb7x',
        defaultMessage: 'What are the best times of year for destination weddings?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage({
        id: 'K4-CflszSYTHVFGOb7Pb',
        defaultMessage: 'That depends on your location! We’ll advise you on the best seasons for weather, availability, and pricing based on your destination (e.g. dry season in Thailand, shoulder season in Europe).',
        description: 'Answer for the FAQs section on the weddings landing page',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'p33ia60bv1lZI1GD3SaX',
        defaultMessage: 'Can you help us choose the right destination or resort?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage({
        id: 'TupMJUPea295YtOmlzoO',
        defaultMessage: 'Absolutely. We’ll guide you based on your budget, vision, guest count, and travel preferences — and only recommend vetted, wedding-friendly properties.',
        description: 'Answer for the FAQs section on the weddings landing page',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'IgXwoEd7iznvSxB1v-Pn',
        defaultMessage: 'Do we need to visit the destination before the wedding?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage({
        id: '5tpBMHVzX2vXF89V51RQ',
        defaultMessage: 'It’s helpful but not required. Many couples plan entirely virtually, and we handle everything on-site to make that possible. If you want a preview trip, we can help you plan one.',
        description: 'Answer for the FAQs section on the weddings landing page',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'f1pLK0mXRcIsfn02O3jP',
        defaultMessage: 'Can the concierge help with special requests (dietary needs, mobility issues, etc.)?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage({
        id: '_zXpHQa-tcUFcdhqNK-y',
        defaultMessage: 'Yes! Whether a guest needs an accessible room, vegetarian meals, or help traveling with young children, our concierge will communicate with the resort and vendors to accommodate every need.',
        description: 'Answer for the FAQs section on the weddings landing page',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'Ls6HvJcSNlR7coIMkBTI',
        defaultMessage: 'Can guests stay somewhere else (not in the room block group rate)?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage({
        id: 'rh8PUysDHyUQrnyd9ZLz',
        defaultMessage: 'Your guests are welcome to stay elsewhere, but booking within our room block helps get your groups better rates. We can also assist with guests wanting to look at alternative accommodation options.',
        description: 'Answer for the FAQs section on the weddings landing page',
      }),
    },
    {
      question: intl.formatMessage({
        id: 'w4jH6q73SzYuY06ZbZc8',
        defaultMessage: 'What about visas, passports, or travel documents?',
        description: 'Question for the FAQs section on the weddings landing page',
      }),
      answer: intl.formatMessage(
        {
          id: 'BXWUMW7HpmdMCf5DE4DP',
          defaultMessage: 'It is your responsibility to make sure your passport is valid for at least six months from the date of return back into Australia as well as ensuring you are aware of all entry requirements for the country you are travelling to. You can find further information on the Australian government website: <url></url>',
          description: 'Answer for the FAQs section on the weddings landing page',
        },
        {
          // oxlint-disable-next-line formatjs/no-literal-string-in-jsx
          url: () => <b><a href="https://www.smartraveller.gov.au/destinations" target="_blank">https://www.smartraveller.gov.au/destinations</a></b>,
        },
      ),
    },
  ], [intl])

  const onVenueInspirationToggle = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const inspiration = (e.currentTarget.dataset.inspiration as VenueInspirationOption) ?? 'featured'
    setSelectedVenueInspiration(inspiration)
  }, [setSelectedVenueInspiration])

  const onVenueInspirationClick = useCallback((e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault()
    const venueOptionIndex = Number(e.currentTarget.dataset.venueOptionIndex)
    const venueOption = visibleVenueInspirations[venueOptionIndex]
    showModal<boolean>(<WeddingVenueInspirationModal
      venueInspirationOption={venueOption}
      onRequestQuoteClick={onBookConsultationClick}
    />)
  }, [visibleVenueInspirations, showModal, onBookConsultationClick])

  const BookConsultationButton = (
    <TextButton kind="primary" size="large" shape="round" onClick={onBookConsultationClick}>
      {bookConsultationButtonLabel}
    </TextButton>
  )

  const onSupportModalConfirm = useCallback(() => {
    onBookConsultationClick()
    setIsSupportModalVisible(false)
  }, [onBookConsultationClick])

  const onSupportModalClose = useCallback(() => {
    setIsSupportModalDismissed(true)
    setIsSupportModalVisible(false)
  }, [])

  useEffect(() => {
    if (!isAboveDesktop || isSupportModalDismissed) return
    let cancelled = false
    wait(20_000).then(() => {
      if (!cancelled) setIsSupportModalVisible(true)
    })
    return () => { cancelled = true }
  }, [isAboveDesktop, isSupportModalDismissed])

  const navigateTo = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const sectionId = e.currentTarget.dataset.toSection
    if (!sectionId) return

    scrollToSection(sectionId)
  }, [])

  return (
    <Root>
      <PaletteSheet paletteType="default">
        {/* Page header */}
        <WeddingPageHeader
          isPastHero
          variant="fixed"
          desktopPalette="default"
          navigationItems={navigationItems}
          primaryButtonLabel={bookConsultationButtonLabel}
          primaryButtonClick={onBookConsultationClick}
        />

        {/* Main Photo */}
        <MainPhotoSection>
          <AspectRatio
            ratio="31:19"
            tabletRatio="180:74"
          >
            <ResponsiveImage
              id="wedding-landing-page-hero"
              alt={intl.formatMessage({
                id: 'HSmqcpAE82vw89RjE8yt',
                defaultMessage: 'A couple holding hands on a landscape',
                description: 'Alt text for the hero image on the weddings landing page',
              })}
              quality="good"
              objectFit="cover"
              fit="center"
            />
          </AspectRatio>
        </MainPhotoSection>

        {/* Content heading */}
        <Sheet desktopBackground="light-background">
          <LayoutContainer variant="medium">
            <HeadingSection
              padding="20 0 40 0"
              background="normal-background"
              tabletPadding="40 80"
              desktopBorderRadius="L"
            >
              <Group gap={28} direction="vertical" horizontalAlign="center">
                <Sheet padding="0 12" tabletPadding="0 0">
                  <Group direction="vertical" gap={4} horizontalAlign="center">
                    <Caption variant="small" weight="bold" format="uppercase" colour="neutral-three">
                      {intl.formatMessage({ defaultMessage: 'Luxury Escapes Weddings', id: '2qTAM1QphUi-e8Myg8Vb', description: 'Section eyebrow label for the content heading on weddings landing page.' })}
                    </Caption>
                    <Heading variant="heading1" wrap="word-wrap" align="center">
                      {intl.formatMessage(
                        { defaultMessage: 'Your <alt>destination wedding</alt>,<lineBreak></lineBreak> the way you pictured it.', id: '5rFRw2QR2FNrwhFRB0xK', description: 'Heading for the greeting section on a wedding page.' },
                        {
                          alt: (chunks: ReactNode) => <Heading variant="heading1alt" wrap="word-wrap" as="span">{chunks}</Heading>,
                          lineBreak: () => !isMobile && <br key="line-break" />,
                        },
                      )}
                    </Heading>
                    <BodyText variant="medium" align="center">
                      {intl.formatMessage({
                        defaultMessage: 'From finding the right venue to booking guest travel, Luxury Escapes coordinates the logistics – so you can focus on what matters.',
                        id: 'QcqyO4D_2gHBrZRpDZDr',
                        description: 'Body text for the content heading on weddings landing page.',
                      })}
                    </BodyText>
                  </Group>
                </Sheet>
                <Group
                  gap={12}
                  direction="vertical-reverse"
                  horizontalAlign="stretch"
                  verticalAlign="center"
                  tabletDirection="horizontal"
                  tabletHorizontalAlign="center"
                  fullWidth={isMobile}
                >
                  <TextButton kind="secondary" size="large" shape="round" onClick={navigateTo} data-to-section={SECTION_IDS.HIGHLIGHTS}>
                    {intl.formatMessage({
                      id: 'N2WfEyFZ7d68-GZboFCg',
                      defaultMessage: 'See how it works',
                      description: "Label for the 'See how it works' button on the weddings landing page",
                    })}
                  </TextButton>
                  {BookConsultationButton}
                </Group>
              </Group>
            </HeadingSection>
          </LayoutContainer>
        </Sheet>

        {/* Highlights section */}
        <Sheet
          padding="40 0 20 0"
          desktopPadding="80 0 64 0"
          background="light-background"
          id={SECTION_IDS.HIGHLIGHTS}
        >
          <LayoutContainer variant="large">
            <Group direction="vertical" gap={40}>
              <Group direction="vertical" gap={8} horizontalAlign="center">
                <Caption variant="small" weight="bold" format="uppercase" colour="neutral-three">
                  {intl.formatMessage({ defaultMessage: 'What we offer', id: 'FkqoKsLBE2zm0IfBPbKq', description: 'Section eyebrow label for the highlights section on the weddings landing page.' })}
                </Caption>
                <Heading variant="heading2" wrap="word-wrap" align="center">
                  {intl.formatMessage({
                    defaultMessage: 'Everything you need, from planning to departure.',
                    id: 'zWDWGks0kaUkn5Y8tLFe',
                    description: 'Heading for the highlights section on the weddings landing page.',
                  })}
                </Heading>
                <BodyText variant="medium" wrap="pre-wrap" align="center">
                  {intl.formatMessage({
                    defaultMessage: 'Every service is complimentary – use one or combine them all for a seamless experience from first booking to final night.',
                    id: '786sqfWy4Yr1m-WrJY05',
                    description: 'Body copy for the highlights section on the weddings landing page.',
                  })}
                </BodyText>
              </Group>
              <Group direction="vertical" gap={24} desktopDirection="horizontal" desktopGap={20}>
                {highlights.list.map((highlight, index) => (
                  <Group key={index.toString()} direction="horizontal" gap={16} desktopDirection="vertical" desktopGap={20}>
                    <div>
                      <RoundedImage
                        imageId={highlight.imageId}
                        alt={highlight.title}
                        width={80}
                        height={80}
                        desktopWidth={233}
                        desktopHeight={311}
                        largeDesktopWidth={270}
                        largeDesktopHeight={360}
                      />
                    </div>
                    <Group direction="vertical" gap={12} verticalAlign="space-between" fullHeight>
                      <Group direction="vertical" gap={12}>
                        <Heading variant="heading4">{highlight.title}</Heading>
                        <BodyText variant="small" colour="light-foreground-alt">{highlight.description}</BodyText>
                      </Group>
                      <TextLink onClick={navigateTo} size="medium" data-to-section={highlight.scrollToSection}>
                        {intl.formatMessage({
                          defaultMessage: 'Learn more',
                          id: 'owHORE7bJAisbvpUHmNo',
                          description: "Label for 'Learn more' buttons on the weddings landing page",
                        })}
                      </TextLink>
                    </Group>
                  </Group>
                ))}
              </Group>
              <Group direction="vertical" verticalAlign="stretch" tabletDirection="horizontal" tabletVerticalAlign="center" gap={12} horizontalAlign="center">
                <BodyText variant="large" align="center">
                  {intl.formatMessage({
                    defaultMessage: 'Have questions about any of our services?',
                    id: 'YK3isl45SwieKtjcPkwx',
                    description: 'Help text for the book consultation button in the "Highlights" section',
                  })}
                </BodyText>
                {BookConsultationButton}
              </Group>
            </Group>
          </LayoutContainer>
        </Sheet>

        {/* Destination & venue section */}
        <Sheet
          padding="0 0"
          desktopPadding="80 0 80 0"
          id={SECTION_IDS.WEDDINGS}
        >
          <Group direction="vertical" gap={40}>
            <CSSBreakpoint max="tablet">
              <RoundedImage
                imageId={highlights.destinationAndVenueSelection.imageId}
                alt={highlights.destinationAndVenueSelection.title}
                width={375}
                height={168}
                fullWidth
                borderless
              />
            </CSSBreakpoint>
            <LayoutContainer variant="large">
              <Group direction="vertical" gap={40} desktopGap={64}>
                <Group direction="vertical" gap={28} desktopDirection="horizontal" desktopGap={64}>
                  <Group direction="vertical" verticalAlign="stretch" desktopVerticalAlign="start" gap={20}>
                    <Group direction="vertical" gap={8}>
                      <Heading variant="heading2">
                        {intl.formatMessage({
                          defaultMessage: 'Destination & venue selection',
                          id: '8mFT0JGlO7FV3Suzm7_P',
                          description: 'Title for the "Destination & venue selection" section on the weddings landing page.',
                        })}
                      </Heading>
                      <BodyText variant="medium" colour="light-foreground-alt">
                        {intl.formatMessage({
                          defaultMessage: 'Your dedicated concierge helps finding the ideal destination, secures the best deals, and coordinates a seamless experience for everyone attending your wedding.',
                          id: '4I4S21fgUfeyNLy6y62c',
                          description: 'Body copy for the "Destination & venue selection" section on the weddings landing page.',
                        })}
                      </BodyText>
                    </Group>
                    <Group direction="vertical" gap={8}>
                      <Group direction="horizontal" verticalAlign="center" gap={12}>
                        <LineSearchIcon />
                        <BodyText variant="medium">
                          {intl.formatMessage({
                            defaultMessage: 'Destination and resort shortlisting',
                            id: 'hbV0p48rYj3y355mqtwU',
                            description: 'Body copy for item in the "Destination & venue selection" section on the weddings landing page.',
                          })}
                        </BodyText>
                      </Group>
                      <Group direction="horizontal" verticalAlign="center" gap={12}>
                        <LineBedIcon />
                        <BodyText variant="medium">
                          {intl.formatMessage({
                            defaultMessage: 'Group accommodation options',
                            id: 'NQQ_7A9ivaGYZGmrDZnB',
                            description: 'Body copy for item in the "Destination & venue selection" section on the weddings landing page.',
                          })}
                        </BodyText>
                      </Group>
                      <Group direction="horizontal" verticalAlign="center" gap={12}>
                        <LinePlaneDepartureIcon />
                        <BodyText variant="medium">
                          {intl.formatMessage({
                            defaultMessage: 'Transport bookings',
                            id: 'F-g_UXrURCkcA7GesRkF',
                            description: 'Body copy for item in the "Destination & venue selection" section on the weddings landing page.',
                          })}
                        </BodyText>
                      </Group>
                      <Group direction="horizontal" verticalAlign="center" gap={12}>
                        <LineUsersAltIcon />
                        <BodyText variant="medium">
                          {intl.formatMessage({
                            defaultMessage: 'Introductions to onsite wedding teams where available',
                            id: '9DVWW0-K6MBqoPFdfERt',
                            description: 'Body copy for item in the "Destination & venue selection" section on the weddings landing page.',
                          })}
                        </BodyText>
                      </Group>
                      <Group direction="horizontal" verticalAlign="center" gap={12}>
                        <LineUserMdIcon />
                        <BodyText variant="medium">
                          {intl.formatMessage({
                            defaultMessage: 'Support at every stage from your personal concierge',
                            id: 'F_pmq4qss773sYd--j8L',
                            description: 'Body copy for item in the "Destination & venue selection" section on the weddings landing page.',
                          })}
                        </BodyText>
                      </Group>
                    </Group>
                    <Group direction="vertical" tabletDirection="horizontal">
                      {BookConsultationButton}
                    </Group>
                  </Group>
                  <CSSBreakpoint min="desktop">
                    <RoundedImage
                      imageId={highlights.destinationAndVenueSelection.imageId}
                      alt={highlights.destinationAndVenueSelection.title}
                      width={538}
                      height={360}
                    />
                  </CSSBreakpoint>
                </Group>
                <Sheet
                  padding="28 28"
                  background="light-background"
                  tabletBorderRadius="L"
                >
                  <Group direction="vertical" tabletDirection="horizontal" gap={20} verticalAlign="center">
                    <LoyaltyCoinsContainer direction="horizontal" verticalAlign="center" horizontalAlign="center">
                      <LoyaltyTierIcon tier="gold" size={67} />
                      <LoyaltyTierIcon tier="platinum" size={67} />
                    </LoyaltyCoinsContainer>
                    <Group direction="vertical" gap={8}>
                      <Heading variant="heading4">
                        {intl.formatMessage({
                          id: 'ViNYDbNJqXc_kRYxXaEa',
                          defaultMessage: 'Get rewarded with Société Gold or Platinum status',
                          description: 'Title for the Société status reward section on the weddings landing page',
                        })}
                      </Heading>
                      <BodyText variant="medium">
                        {intl.formatMessage({
                          id: 'v5XQummiQH-y4OPDgsBA',
                          defaultMessage: 'When you book your wedding with Luxury Escapes you’ll earn Société points & status — giving you access to exclusive travel rewards.',
                          description: 'Description for the Société status reward section on the weddings landing page',
                        })}
                      </BodyText>
                    </Group>
                    <TextButton kind="secondary" size="large" shape="round" to="/societe" target="_blank">
                      {intl.formatMessage({
                        defaultMessage: 'Learn more',
                        id: 'owHORE7bJAisbvpUHmNo',
                        description: "Label for 'Learn more' buttons on the weddings landing page",
                      })}
                    </TextButton>
                  </Group>
                </Sheet>
              </Group>
            </LayoutContainer>
          </Group>
        </Sheet>

        {/* Gift registry & wedding website section */}
        <Sheet
          padding="24 0 40 0"
          desktopPadding="64 0"
          id={SECTION_IDS.GIFT_REGISTRY_AND_WEBSITE}
        >
          <LayoutContainer variant="large">
            <Group direction="vertical" gap={12} desktopDirection="horizontal" desktopGap={48} horizontalAlign="center">
              <GiftRegistryImagesContainer>
                <div className="left-image">
                  <RoundedImage
                    imageId="wedding-landing-page-gift-registry-tile-1"
                    width={75}
                    height={69}
                    desktopWidth={128}
                    desktopHeight={118}
                    fullWidth
                    fullHeight
                    borderless
                  />
                </div>
                <div className="main-image">
                  <VimeoVideo
                    vimeoVideoId="1208631868"
                    query={{ background: true }}
                  />
                </div>
                <div className="right-image">
                  <RoundedImage
                    imageId="wedding-landing-page-gift-registry-tile-2"
                    width={85}
                    height={86}
                    desktopWidth={145}
                    desktopHeight={146}
                    borderless
                  />
                </div>
              </GiftRegistryImagesContainer>
              <Group direction="vertical" gap={40}>
                <Group direction="vertical" gap={20}>
                  <Group direction="vertical" gap={8}>
                    <Heading variant="heading2">
                      {intl.formatMessage({
                        id: '5K6TBxX7l5tRTN_canmG',
                        defaultMessage: 'Gift registry & wedding website',
                        description: 'Title for the "Gift registry & wedding website" section on the weddings landing page',
                      })}
                    </Heading>
                    <BodyText variant="large" colour="light-foreground-alt">
                      {intl.formatMessage({
                        id: 'LsajJ1ii_Y_ERSwO523D',
                        defaultMessage: 'A personalised wedding website gives guests everything they need in one place – and somewhere to contribute before the day.',
                        description: 'Description for the "Gift registry & wedding website" section on the weddings landing page',
                      })}
                    </BodyText>
                  </Group>
                  <Group direction="vertical" gap={8}>
                    <Group direction="horizontal" verticalAlign="center" gap={12}>
                      <LineLaptopIcon />
                      <BodyText variant="medium">
                        {intl.formatMessage({
                          defaultMessage: 'Personalised wedding website with event and travel details',
                          id: 'j6vLG3561M-3LW2qRSDF',
                          description: 'Body copy for item in the "Gift registry & wedding website" section on the weddings landing page.',
                        })}
                      </BodyText>
                    </Group>
                    <Group direction="horizontal" verticalAlign="center" gap={12}>
                      <LineGiftIcon />
                      <BodyText variant="medium">
                        {intl.formatMessage({
                          defaultMessage: 'Registry across thousands of hotels, experiences and products',
                          id: 'leR7N9t03WsZ9O0qoDH-',
                          description: 'Body copy for item in the "Gift registry & wedding website" section on the weddings landing page.',
                        })}
                      </BodyText>
                    </Group>
                    <Group direction="horizontal" verticalAlign="center" gap={12}>
                      <LineHeartCardIcon />
                      <BodyText variant="medium">
                        {intl.formatMessage({
                          defaultMessage: 'Guests can contribute to your honeymoon or Luxury Escapes travel credit',
                          id: 'V7vE3hGxYndKnJYbTzvF',
                          description: 'Body copy for item in the "Gift registry & wedding website" section on the weddings landing page.',
                        })}
                      </BodyText>
                    </Group>
                    <Group direction="horizontal" verticalAlign="center" gap={12}>
                      <LineLinkIcon />
                      <BodyText variant="medium">
                        {intl.formatMessage({
                          defaultMessage: 'Shareable link for invitations',
                          id: '9yNNd8BV1d_1T6ZZe3x3',
                          description: 'Body copy for item in the "Gift registry & wedding website" section on the weddings landing page.',
                        })}
                      </BodyText>
                    </Group>
                  </Group>
                </Group>
                <Group direction="vertical" gap={12} tabletDirection="horizontal">
                  <TextButton kind="primary" size="large" shape="round" to={WEDDING_SETUP_PATH} target="_blank">
                    {intl.formatMessage({
                      id: '2mpHwOHgE9QCaFWzkF4V',
                      defaultMessage: 'Create your free gift registry',
                      description: 'Primary CTA for the "Gift registry & wedding website" section on the weddings landing page',
                    })}
                  </TextButton>
                  <TextButton kind="secondary" size="large" shape="round" href={SAMPLE_COUPLE_PAGE_URL} target="_blank">
                    {intl.formatMessage({
                      id: 'HfHeNGiLVWum363npb4I',
                      defaultMessage: 'View an example',
                      description: 'Secondary CTA for the "Gift registry & wedding website" section on the weddings landing page',
                    })}
                  </TextButton>
                </Group>
              </Group>
            </Group>
          </LayoutContainer>
        </Sheet>

        {/* Honeymoon planning section */}
        <Sheet
          desktopPadding="40 0 64 0"
          id={SECTION_IDS.HONEYMOON}
        >
          <HoneymoonPlanningSectionLayoutContainer variant="large" relative>
            <div className="image-wrapper">
              <CSSBreakpoint max="mobile">
                <RoundedImage
                  imageId={highlights.honeymoonInspirationAndPlanning.imageId}
                  alt={highlights.honeymoonInspirationAndPlanning.title}
                  width={375}
                  height={663}
                  fullWidth
                  fullHeight
                  borderless
                />
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <RoundedImage
                  imageId={highlights.honeymoonInspirationAndPlanning.imageId}
                  alt={highlights.honeymoonInspirationAndPlanning.title}
                  width={1140}
                  height={478}
                  fullWidth
                  fullHeight
                />
              </CSSBreakpoint>
            </div>
            <Sheet padding="20 0 64 0" tabletPadding="56 40">
              <Group direction="horizontal">
                <Sheet padding="24 24" tabletPadding="40 40" borderRadius="L" background="normal-background" className="content-container">
                  <Group direction="vertical" gap={20}>
                    <Group direction="vertical" gap={8}>
                      <Heading variant="heading2">
                        {intl.formatMessage({
                          id: 'sbVCrhzeN5l1_QpLTUnx',
                          defaultMessage: 'Honeymoon planning',
                          description: 'Title for the "Honeymoon planning" section on the weddings landing page',
                        })}
                      </Heading>
                      <BodyText variant="large" colour="light-foreground-alt">
                        {intl.formatMessage({
                          id: 'WtVXpkXh0DsvWkwPxn5L',
                          defaultMessage: "Tell us where you've always wanted to go. We'll build the itinerary around that.",
                          description: 'Description for the "Honeymoon planning" section on the weddings landing page',
                        })}
                      </BodyText>
                    </Group>
                    <Group direction="vertical" gap={16}>
                      <Group direction="vertical" gap={8}>
                        <Group direction="horizontal" verticalAlign="center" gap={12}>
                          <LineSearchIcon />
                          <BodyText variant="medium">
                            {intl.formatMessage({
                              defaultMessage: 'Destination and hotel recommendations',
                              id: 'dTga85GwO3iQCdq9O4qo',
                              description: 'Body copy for item in the "Honeymoon planning" section on the weddings landing page.',
                            })}
                          </BodyText>
                        </Group>
                        <Group direction="horizontal" verticalAlign="center" gap={12}>
                          <LineCalendarHeartIcon />
                          <BodyText variant="medium">
                            {intl.formatMessage({
                              defaultMessage: 'Bespoke itinerary planning',
                              id: 'ElyOy1rT5aPUj1fKXEnL',
                              description: 'Body copy for item in the "Honeymoon planning" section on the weddings landing page.',
                            })}
                          </BodyText>
                        </Group>
                        <Group direction="horizontal" verticalAlign="center" gap={12}>
                          <LinePlaneDepartureIcon />
                          <BodyText variant="medium">
                            {intl.formatMessage({
                              defaultMessage: 'Transport bookings and logistics',
                              id: 'dDxNyXUG3IoxdVQ9AUfs',
                              description: 'Body copy for item in the "Honeymoon planning" section on the weddings landing page.',
                            })}
                          </BodyText>
                        </Group>
                        <Group direction="horizontal" verticalAlign="center" gap={12}>
                          <LineUserMdIcon />
                          <BodyText variant="medium">
                            {intl.formatMessage({
                              defaultMessage: 'Concierge support throughout',
                              id: 'Knf5Xn-NNSdyfNcpWNTD',
                              description: 'Body copy for item in the "Honeymoon planning" section on the weddings landing page.',
                            })}
                          </BodyText>
                        </Group>
                      </Group>
                      <Group direction="horizontal">
                        {BookConsultationButton}
                      </Group>
                    </Group>
                  </Group>
                </Sheet>
              </Group>
            </Sheet>
          </HoneymoonPlanningSectionLayoutContainer>
        </Sheet>

        {/* Venue inspirations section */}
        {hasVenueInspirationsSection && (
          <Sheet
            padding="64 0 0 0"
            tabletPadding="64 0 64 0"
            background="light-background"
            id={SECTION_IDS.INSPIRATION}
          >
            <Group direction="vertical" gap={40}>
              <LayoutContainer variant="large">
                <Group direction="vertical" gap={20} desktopHorizontalAlign="center">
                  <Group direction="vertical" gap={8} horizontalAlign="center">
                    <Caption variant="small" weight="bold" format="uppercase" colour="neutral-three">
                      {intl.formatMessage({ defaultMessage: 'Venue inspirations', id: 'I2EtbW3TUnvgRv86V_iJ', description: 'Section eyebrow label for the "Venue inspirations" section on weddings landing page.' })}
                    </Caption>
                    <Heading variant="heading2" wrap="word-wrap" align="center">
                      {intl.formatMessage({ defaultMessage: 'Inspired places to say “I do”', id: 'BaPHb3Ih2X6HPGdlNyp_', description: 'Heading for the "Venue inspirations" section on a wedding page.' })}
                    </Heading>
                  </Group>
                  <LayoutContainer variant="medium">
                    <BodyText variant="medium" align="center" colour="light-foreground-alt">
                      {intl.formatMessage({
                        defaultMessage: 'From island resorts to European lakefront estates, our wedding concierge can help match you with destinations and venues that suit your style, budget and celebration plans.',
                        id: '5pa7AAFRsJOhABV4b5AZ',
                        description: 'Body text for the "Venue inspirations" section on weddings landing page.',
                      })}
                    </BodyText>
                  </LayoutContainer>
                  <Carousel gutterSize={8} gutterStyle="overflow" gap={12}>
                    <VenueInspirationToggleButton
                      kind="secondary"
                      title={intl.formatMessage({
                        defaultMessage: 'Featured venues',
                        id: 'kgGwK4K9X6wxXSiGEOZY',
                        description: 'Toggle button for the "Venue inspirations" section on weddings landing page.',
                      })}
                      startIcon={<LineAwardIcon />}
                      orientation="horizontal"
                      data-inspiration="featured"
                      onClick={onVenueInspirationToggle}
                      isSelected={selectedVenueInspiration === 'featured'}
                    />
                    <VenueInspirationToggleButton
                      kind="secondary"
                      title={intl.formatMessage({
                        defaultMessage: 'Beachfront',
                        id: 'vHQ1sPZcUKBpUgBt2eYF',
                        description: 'Toggle button for the "Venue inspirations" section on weddings landing page.',
                      })}
                      startIcon={<LineWaterIcon />}
                      orientation="horizontal"
                      data-inspiration="beachfront"
                      onClick={onVenueInspirationToggle}
                      isSelected={selectedVenueInspiration === 'beachfront'}
                    />
                    <VenueInspirationToggleButton
                      kind="secondary"
                      title={intl.formatMessage({
                        defaultMessage: 'Cityscapes',
                        id: 'nB6V9OpViD__cPqnR0y6',
                        description: 'Toggle button for the "Venue inspirations" section on weddings landing page.',
                      })}
                      startIcon={<LineBuildingIcon />}
                      orientation="horizontal"
                      data-inspiration="cityEscapes"
                      onClick={onVenueInspirationToggle}
                      isSelected={selectedVenueInspiration === 'cityEscapes'}
                    />
                    <VenueInspirationToggleButton
                      kind="secondary"
                      title={intl.formatMessage({
                        defaultMessage: 'Garden/Winery',
                        id: '9G6wLyK81mMvn15iW_xP',
                        description: 'Toggle button for the "Venue inspirations" section on weddings landing page.',
                      })}
                      startIcon={<LineTreesIcon />}
                      orientation="horizontal"
                      data-inspiration="garden_winery"
                      onClick={onVenueInspirationToggle}
                      isSelected={selectedVenueInspiration === 'garden_winery'}
                    />
                    <VenueInspirationToggleButton
                      kind="secondary"
                      title={intl.formatMessage({
                        defaultMessage: 'Intimate weddings',
                        id: '_TiqR8sEqr8j_VE_EUay',
                        description: 'Toggle button for the "Venue inspirations" section on weddings landing page.',
                      })}
                      startIcon={<LineUsersAltIcon />}
                      orientation="horizontal"
                      data-inspiration="intimateWeddings"
                      onClick={onVenueInspirationToggle}
                      isSelected={selectedVenueInspiration === 'intimateWeddings'}
                    />
                  </Carousel>
                </Group>
              </LayoutContainer>
              <FullWidthCarousel>
                {shouldShowVenueInspirationsLoading && Array.from({ length: 3 }).map((_, index) => (
                  <VenueInspirationLoadingTile key={index.toString()} borderRadius="L" background="normal-background">
                    <LoadingBox className="image-placeholder" />
                    <Sheet padding="20 20" background="normal-background" className="tile-content">
                      <Group direction="vertical" gap={12}>
                        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
                          <Group direction="vertical" gap={4} fullWidth>
                            <TextLoadingBox typography="body-medium" width="45%" />
                            <TextLoadingBox typography="body-large" width="80%" />
                          </Group>
                          <LoadingBox width={36} height={24} />
                        </Group>
                        <Divider kind="primary" direction="horizontal" />
                        <Group direction="vertical" gap={8}>
                          <TextLoadingBox typography="body-small" width="70%" />
                          <TextLoadingBox typography="body-small" width="62%" />
                        </Group>
                        <Divider kind="primary" direction="horizontal" />
                        <Group direction="vertical" gap={8}>
                          <TextLoadingBox typography="body-medium" width="95%" />
                          <TextLoadingBox typography="body-medium" width="88%" />
                          <TextLoadingBox typography="body-medium" width="76%" />
                        </Group>
                        <Group direction="horizontal">
                          <TextButtonLoadingSkeleton width="10ch" />
                        </Group>
                      </Group>
                    </Sheet>
                  </VenueInspirationLoadingTile>
                ))}
                {!shouldShowVenueInspirationsLoading && visibleVenueInspirations.map((option, index) => (
                  <VenueInspirationTile key={option.offerId} borderRadius="L">
                    <Group direction="vertical">
                      <VenueInspirationTileGallery
                        images={option.images}
                        showImageTitles={false}
                        imageParams={venueInspirationTileImageProps}
                        size="small"
                        disableLightbox
                        arrowStyle="ghost"
                      />
                    </Group>
                    <Sheet padding="20 20" background="normal-background" className="tile-content">
                      <Group direction="vertical" gap={8} fullHeight>
                        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
                          <Group direction="vertical">
                            <BodyText variant="medium">{option.location}</BodyText>
                            <BodyText variant="large" weight="bold">{option.title}</BodyText>
                          </Group>
                          {!!option.rating && <NumberRating rating={option.rating.score} onlyNumber variant="xs" />}
                        </Group>
                        <Divider kind="primary" direction="horizontal" />
                        <Group direction="vertical" gap={2} desktopDirection="horizontal" desktopGap={12} wrap="wrap" style={{ rowGap: 0 }}>
                          <Group direction="horizontal" gap={4} verticalAlign="center">
                            <LineUsersAltIcon size="S" colour="light-foreground-alt" />
                            <Group direction="horizontal" verticalAlign="center" gap={4}>
                              <BodyText variant="small" weight="bold" colour="light-foreground-alt">
                                {intl.formatMessage({
                                  defaultMessage: 'Capacity:',
                                  id: '9GzDnUvvXkzubQiY31zI',
                                  description: 'Guest capacity label for the inspiration tile in the "Venue inspirations" section on weddings landing page.',
                                })}
                              </BodyText>
                              <BodyText variant="small" colour="light-foreground-alt">{option.guestCapacity}</BodyText>
                            </Group>
                          </Group>
                          <CSSBreakpoint min="desktop">
                            {/* oxlint-disable-next-line formatjs/no-literal-string-in-jsx */}
                            <BodyText variant="small">·</BodyText>
                          </CSSBreakpoint>
                          <Group direction="horizontal" gap={4} verticalAlign="center">
                            <LineCalendarCheckIcon size="S" colour="light-foreground-alt" />
                            <Group direction="horizontal" verticalAlign="center" gap={4}>
                              <BodyText variant="small" weight="bold" colour="light-foreground-alt">
                                {intl.formatMessage({
                                  defaultMessage: 'Best in:',
                                  id: 'Rya5rrXaD6-qT7qa7Tyl',
                                  description: 'Label for the best season for the inspiration tile in the "Venue inspirations" section on weddings landing page.',
                                })}
                              </BodyText>
                              <WeddingVenueSeason
                                season={option.season}
                                seasonHint={option.seasonHint}
                                variant="small"
                              />
                            </Group>
                          </Group>
                        </Group>
                        <Divider kind="primary" direction="horizontal" />
                        <Group direction="vertical" gap={4} fullHeight>
                          {option.highlights.map((highlight, index) => (
                            <Group key={index.toString()} direction="horizontal" gap={8}>
                              <LineCheckIcon size="S" colour="light-foreground-alt" />
                              <BodyText variant="medium" colour="light-foreground-alt">{highlight}</BodyText>
                            </Group>
                          ))}
                        </Group>
                        <Group direction="horizontal">
                          <TextLink
                            size="medium"
                            colour="neutral-two"
                            data-venue-option-index={index}
                            onClick={onVenueInspirationClick}
                          >
                            {intl.formatMessage({
                              defaultMessage: 'Learn more',
                              id: 'owHORE7bJAisbvpUHmNo',
                              description: "Label for 'Learn more' buttons on the weddings landing page",
                            })}
                          </TextLink>
                        </Group>
                      </Group>
                    </Sheet>
                  </VenueInspirationTile>
                ))}
                <VenueInspirationCTATile background="normal-background" borderRadius="L" padding="20 40">
                  <Group direction="vertical" gap={28} horizontalAlign="center" verticalAlign="center" fullHeight>
                    <VenueInspirationImagesWrapper direction="horizontal" horizontalAlign="center" verticalAlign="center">
                      <RoundedImage
                        imageId="wedding-landing-page-venue-inspiration-CTA-tile-1"
                        width={90}
                        height={90}
                      />
                      <RoundedImage
                        imageId="wedding-landing-page-venue-inspiration-CTA-tile-2"
                        width={90}
                        height={90}
                      />
                    </VenueInspirationImagesWrapper>
                    <Group direction="vertical" horizontalAlign="center">
                      <Heading variant="heading1" align="center">
                        {intl.formatMessage({
                          defaultMessage: 'Unlock our full collection',
                          id: 'vWHj59_DWDSYJrVZ-OZm',
                          description: 'Header for the CTA tile in the venue inspirations list on the weddings landing page',
                        })}
                      </Heading>
                      <BodyText variant="medium" colour="light-foreground-alt" align="center">
                        {intl.formatMessage({
                          defaultMessage: 'Contact our dedicated concierge team for a bespoke recommendation based on your preferences.',
                          id: '4jPjWf6n90qehGLZVCuM',
                          description: 'Description for the CTA tile in the venue inspirations list on the weddings landing page',
                        })}
                      </BodyText>
                    </Group>
                    <Group direction="vertical" desktopDirection="horizontal">
                      {BookConsultationButton}
                    </Group>
                  </Group>
                </VenueInspirationCTATile>
              </FullWidthCarousel>
              <LayoutContainer variant="large">
                <VenueInspirationCTABannerContainer
                  padding="28 28"
                  background="normal-background"
                  tabletBorderRadius="L"
                >
                  <Group direction="vertical" tabletDirection="horizontal" gap={20} verticalAlign="center">
                    <VenueInspirationImagesWrapper direction="horizontal" horizontalAlign="center" verticalAlign="center">
                      <RoundedImage
                        imageId="wedding-landing-page-venue-inspiration-cta-1"
                        width={80}
                        height={80}
                      />
                      <RoundedImage
                        imageId="wedding-landing-page-venue-inspiration-cta-2"
                        width={80}
                        height={80}
                      />
                    </VenueInspirationImagesWrapper>
                    <Group direction="vertical" gap={8} fullWidth>
                      <Heading variant="heading4">
                        {intl.formatMessage({
                          id: 'QlqtcynMN7soYMmMGqSv',
                          defaultMessage: 'Not sure where to begin? Your concierge can help.',
                          description: 'Title for the CTE banner in "Venue inspirations" section on the weddings landing page',
                        })}
                      </Heading>
                      <BodyText variant="medium">
                        {intl.formatMessage({
                          id: 'JyEbhYUCGGSovkBgVKgP',
                          defaultMessage: "Tell us what you have in mind and we'll take it from there.",
                          description: 'Description for the CTE banner in "Venue inspirations" section on the weddings landing page',
                        })}
                      </BodyText>
                    </Group>
                    {BookConsultationButton}
                  </Group>
                </VenueInspirationCTABannerContainer>
              </LayoutContainer>
            </Group>
          </Sheet>
        )}

        {/* Reviews section */}
        <Sheet padding="64 0">
          <LayoutContainer variant="large">
            <Group direction="vertical" gap={40}>
              <Heading variant="heading2" wrap="word-wrap" align="center">
                {intl.formatMessage({ defaultMessage: 'What our happy couples say', id: 'NsX-IC0R6zk-0Ovw7VQ7', description: 'Heading for the "Reviews" on weddings landing page.' })}
              </Heading>
              <Carousel pageSize={1} width="100%">
                <ReviewContainer background="light-background" borderRadius="L">
                  <Group direction="vertical-reverse" desktopDirection="horizontal">
                    <Sheet padding="24 24" desktopPadding="40 40">
                      <Group direction="vertical" gap={20}>
                        <Group direction="vertical" gap={12}>
                          <WeddingQuoteOpenIcon colour="neutral-three" size="L" />
                          <BodyText variant="large" colour="light-foreground-alt">
                            {intl.formatMessage({
                              id: 'cGk2nYxDzbPJqLmKd-v_',
                              defaultMessage: 'I spoke with Ben and the team at Luxury Escapes Chadstone when planning my destination wedding in Bali. They listened to exactly what I wanted and we found the perfect accommodation for my fiancé and I, and our 50 guests. The process was absolutely seamless and the team are fantastic to deal with."',
                              description: 'Review with close quote in the "Reviews" section on weddings landing page.',
                            })}
                          </BodyText>
                        </Group>
                        <Group direction="vertical">
                          <BodyText variant="medium" weight="bold">
                            {intl.formatMessage({
                              id: 'XPZnwUluXpgPAtC5RhUc',
                              defaultMessage: 'Hanna from Sydney',
                              description: 'Reviewer in the "Reviews" section on weddings landing page.',
                            })}
                          </BodyText>
                          <BodyText variant="medium">
                            {intl.formatMessage(
                              {
                                id: 'bMMecPRsi6DMZNDTXdGM',
                                defaultMessage: 'Venue: <bold>Seascape Resort & Spa Uluwatu</bold>',
                                description: 'Venue in the "Reviews" section on weddings landing page.',
                              },
                              {
                                bold: (chunks) => <b>{chunks}</b>,
                              },
                            )}
                          </BodyText>
                          <BodyText variant="medium">
                            {intl.formatMessage(
                              {
                                id: 'dECKa9fgk98q3hAupmR9',
                                defaultMessage: 'Wedding concierge: <bold>Ben</bold>',
                                description: 'Wedding concierge in the "Reviews" section on weddings landing page.',
                              },
                              {
                                bold: (chunks) => <b>{chunks}</b>,
                              },
                            )}
                          </BodyText>
                        </Group>
                      </Group>
                    </Sheet>
                    <Group direction="horizontal" className="image-wrapper">
                      <CSSBreakpoint max="tablet">
                        <RoundedImage
                          imageId="wedding-landing-page-review-1"
                          width={335}
                          height={200}
                          fullWidth
                          borderless
                        />
                      </CSSBreakpoint>
                      <CSSBreakpoint min="desktop">
                        <RoundedImage
                          imageId="wedding-landing-page-review-1"
                          width={440}
                          height={324}
                          largeDesktopWidth={540}
                          largeDesktopHeight={324}
                          fullHeight
                          borderless
                        />
                      </CSSBreakpoint>
                    </Group>
                  </Group>
                </ReviewContainer>
              </Carousel>
            </Group>
          </LayoutContainer>
        </Sheet>

        {/* Concierge section */}
        <Sheet padding="0 0 64 0" desktopPadding="64 0" background="light-background" id={SECTION_IDS.CONCIERGE}>
          <Group direction="vertical" gap={24}>
            <CSSBreakpoint max="tablet">
              <RoundedImage
                imageId={highlights.dedicatedConciergeAndSupport.imageId}
                alt={highlights.dedicatedConciergeAndSupport.title}
                width={375}
                height={200}
                fullWidth
                borderless
                fit="top"
              />
            </CSSBreakpoint>
            <LayoutContainer variant="large">
              <Group direction="vertical" desktopDirection="horizontal" gap={48}>
                <CSSBreakpoint min="desktop">
                  <RoundedImage
                    imageId={highlights.dedicatedConciergeAndSupport.imageId}
                    alt={highlights.dedicatedConciergeAndSupport.title}
                    width={570}
                    height={310}
                  />
                </CSSBreakpoint>
                <Group direction="vertical" verticalAlign="stretch" desktopVerticalAlign="start" gap={40}>
                  <Group direction="vertical" gap={20}>
                    <Group direction="vertical" gap={8}>
                      <Heading variant="heading2">
                        {intl.formatMessage({
                          defaultMessage: 'Personal concierge',
                          id: 'H1-P8XAKn6zLGNPS9CpM',
                          description: 'Title for the "Concierge" section on the weddings landing page.',
                        })}
                      </Heading>
                      <BodyText variant="medium" colour="light-foreground-alt">
                        {intl.formatMessage({
                          defaultMessage: 'One person. Every question, booking and request – from the moment you enquire to the day you leave.',
                          id: 'I8K6zCnyhvMhrsp86we4',
                          description: 'Body copy for the "Concierge" section on the weddings landing page.',
                        })}
                      </BodyText>
                    </Group>
                    <Group direction="vertical" gap={8}>
                      <Group direction="horizontal" verticalAlign="center" gap={12}>
                        <LineBedIcon />
                        <BodyText variant="medium">
                          {intl.formatMessage({
                            defaultMessage: 'Guest travel and accommodation bookings',
                            id: 'PCUekozPpNv18fhx3cZ1',
                            description: 'Body copy for item in the "Concierge" section on the weddings landing page.',
                          })}
                        </BodyText>
                      </Group>
                      <Group direction="horizontal" verticalAlign="center" gap={12}>
                        <LineCommentInfoIcon />
                        <BodyText variant="medium">
                          {intl.formatMessage({
                            defaultMessage: 'Ongoing support for booking enquiries and changes',
                            id: 'JRQEwjvR9QceXWTEtbpP',
                            description: 'Body copy for item in the "Concierge" section on the weddings landing page.',
                          })}
                        </BodyText>
                      </Group>
                      <Group direction="horizontal" verticalAlign="center" gap={12}>
                        <LineFileCheckAltIcon />
                        <BodyText variant="medium">
                          {intl.formatMessage({
                            defaultMessage: 'Coordination between venues, suppliers and guests',
                            id: 'ERmEEsPMhgwTgFYVFrPq',
                            description: 'Body copy for item in the "Concierge" section on the weddings landing page.',
                          })}
                        </BodyText>
                      </Group>
                      <Group direction="horizontal" verticalAlign="center" gap={12}>
                        <LineUserMdIcon />
                        <BodyText variant="medium">
                          {intl.formatMessage({
                            defaultMessage: 'Available from first consultation to departure',
                            id: 'sc3ElLYQaPJKJgil1dKf',
                            description: 'Body copy for item in the "Concierge" section on the weddings landing page.',
                          })}
                        </BodyText>
                      </Group>
                    </Group>
                  </Group>
                  <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="center" desktopHorizontalAlign="start">
                    {BookConsultationButton}
                  </Group>
                </Group>
              </Group>
            </LayoutContainer>
          </Group>
        </Sheet>

        {/* FAQs section */}
        <Sheet padding="64 0" id={SECTION_IDS.FAQS}>
          <LayoutContainer variant="medium">
            <Group direction="vertical" gap={40}>
              <Heading variant="heading2" align="center">
                {intl.formatMessage({
                  defaultMessage: 'FAQs',
                  id: 'OCKGKL_KmScZdpNeLnLN',
                  description: 'Title for the "FAQs" section on the weddings landing page.',
                })}
              </Heading>
              <Group direction="vertical" gap={16} fullWidth>
                {faqs.map((qa, index) => (
                  <Accordion
                    key={index}
                    heading={qa.question}
                    size="small"
                  >
                    <BodyText variant="large" wrap="pre-wrap">
                      {qa.answer}
                    </BodyText>
                  </Accordion>
                ))}
              </Group>
            </Group>
          </LayoutContainer>
        </Sheet>
      </PaletteSheet>
      <WeddingLandingPageSupportModal isVisible={isSupportModalVisible} onBookConsultationClick={onSupportModalConfirm} onClose={onSupportModalClose} />
    </Root>
  )
}

export default WeddingsLandingPage
